import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import { IUserInfo } from '../../types/Auth/authInterface';
import customFetchBase from '../expiredToken/expiredToken';
import {
  IGetAllUsersResponse,
  IUserPendingActivation
} from '../../types/users';

const customFetch = customFetchBase(config.baseUrlCustomers);

export const apiUsers = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getUserInfo: builder.mutation<IUserInfo, string>({
      query: (email) => ({
        url: `api/user/GetUserInfoById/${encodeURIComponent(email)}`,
        method: 'GET'
      })
    }),
    getUsersAuth0: builder.mutation<
      IGetAllUsersResponse,
      {
        page: number;
        pageSize: number;
        filterByLabel: string;
        filterByRole: string;
        filterByAuthorized: string;
      }
    >({
      query: (payload) => ({
        url: `api/user/GetAllUsersWithSystemRoleInfo`,
        method: 'GET',
        params: payload
      })
    }),
    postUserInfo: builder.mutation<{ id: number }, IUserInfo>({
      query: (payload) => ({
        url: 'api/user',
        method: 'POST',
        body: payload
      })
    }),
    updateUserInfo: builder.mutation<any, IUserInfo>({
      query: (payload) => ({
        url: 'api/user',
        method: 'PUT',
        body: payload
      })
    }),
    getAllUsers: builder.mutation<any, {}>({
      query: () => ({
        url: 'api/User/AllUsers',
        method: 'GET'
      })
    }),
    getLocationsUsersByLocationId: builder.mutation<any, {}>({
      query: (locationId: number) => ({
        url: `api/Location/GetLocationUsersByLocationId/${locationId}`,
        method: 'GET'
      })
    }),
    deleteSystemUser: builder.mutation<any, number | undefined>({
      query: (userId: number) => ({
        url: `api/User?userId=${userId}`,
        method: 'DELETE'
      })
    }),
    toggleAuthorizedStatusByUserId: builder.mutation<
      any,
      { userId: number; newStatus: boolean }
    >({
      query: (data) => ({
        url: `api/User/AuthorizedStatus`,
        method: 'PUT',
        body: { userId: data.userId, authorized: data.newStatus }
      })
    }),
    deleteMultipleUsers: builder.mutation<any, number[]>({
      query: (usersIds) => ({
        url: `api/User/Multi?ids=${usersIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    putChangePassword: builder.mutation<
      null,
      { currentPassword: string; newPassword: string }
    >({
      query: (payload) => ({
        url: 'api/User/ChangeCurrentUserPassword',
        method: 'PUT',
        body: payload
      }),
      transformErrorResponse: (response: any) => {
        return response.data.message;
      }
    }),
    changeUserPassword: builder.mutation<
      null,
      { email: string; password: string }
    >({
      query: (payload) => ({
        url: 'api/User/Password',
        method: 'PUT',
        body: payload
      })
    }),
    deleteMyAccount: builder.mutation<any, void>({
      query: () => ({
        url: 'api/User/MyAccount',
        method: 'DELETE'
      })
    }),
    getUserPendingActivation: builder.mutation<IUserPendingActivation[], void>({
      query: () => ({
        url: `api/User/UnverifiedAccounts`,
        method: 'GET'
      })
    }),
    getUserOptCode: builder.mutation<[], void>({
      query: () => ({
        url: `api/User/AllSentOTPs`,
        method: 'GET'
      })
    }),
    deleteUsersPendingActivation: builder.mutation<any, number[]>({
      query: (ids) => ({
        url: `api/User/EmailToVerify?ids=${ids.join(',')}`,
        method: 'DELETE'
      })
    }),
    authorizeApplication: builder.mutation<
      any,
      { userId: number; appId: number }
    >({
      query: (payload) => ({
        url: `api/User/AuthorizedApp`,
        method: 'POST',
        body: payload
      })
    }),
    deleteAuthorizedApp: builder.mutation<any, number>({
      query: (appId) => ({
        url: `api/User/AuthorizedApp/${appId}`,
        method: 'DELETE'
      })
    })
  })
});

export const {
  useGetUserInfoMutation,
  usePostUserInfoMutation,
  useUpdateUserInfoMutation,
  useGetAllUsersMutation,
  useGetUsersAuth0Mutation,
  useGetLocationsUsersByLocationIdMutation,
  useDeleteSystemUserMutation,
  useToggleAuthorizedStatusByUserIdMutation,
  useDeleteMultipleUsersMutation,
  usePutChangePasswordMutation,
  useChangeUserPasswordMutation,
  useDeleteMyAccountMutation,
  useGetUserPendingActivationMutation,
  useDeleteUsersPendingActivationMutation,
  useAuthorizeApplicationMutation,
  useDeleteAuthorizedAppMutation,
  useGetUserOptCodeMutation
} = apiUsers;
